import React from 'react'
import {
  Trash2,
  Plus,
  Minus,
  Pencil,
  Check,
  X,
  RotateCcw,
  Warehouse
} from 'lucide-react'
import type { CartItem } from '../../types'
import type { ShipmentMode } from './ui/mode-theme'

interface ProductColor {
  id: string
  name: string
  hex_code?: string
}

interface ProductSize {
  id: string
  name: string
}

interface InhouseCartLineProps {
  cartItem: CartItem
  shipmentMode: ShipmentMode
  shouldShowPricing: boolean
  warehouseLocationName?: string
  itemColor?: ProductColor
  itemSize?: ProductSize
  originalPrice?: number
  availableQty: number
  isEditing: boolean
  editingPriceValue: string
  onRemove: (id: string) => void
  onUpdateQuantity: (id: string, qty: number) => void
  onPriceEditStart: (id: string, price: number) => void
  onPriceEditSave: (id: string) => void
  onPriceEditCancel: () => void
  onPriceEditChange: (value: string) => void
  onResetPrice: (id: string) => void
  allowOverStock?: boolean
}

export function InhouseCartLine({
  cartItem,
  shipmentMode,
  shouldShowPricing,
  warehouseLocationName,
  itemColor,
  itemSize,
  originalPrice,
  availableQty,
  isEditing,
  editingPriceValue,
  onRemove,
  onUpdateQuantity,
  onPriceEditStart,
  onPriceEditSave,
  onPriceEditCancel,
  onPriceEditChange,
  onResetPrice,
  allowOverStock = false
}: InhouseCartLineProps) {
  const isPriceModified = cartItem.priceModified || (originalPrice != null && cartItem.sell_price !== originalPrice)

  return (
    <div
      className={`p-3 rounded-lg border transition-all ${
        isPriceModified
          ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
          : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
            {cartItem.name}
          </p>
          {(itemColor || itemSize) && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {itemColor && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <span
                    className="w-2.5 h-2.5 rounded-full border border-gray-300 dark:border-gray-600"
                    style={{ backgroundColor: itemColor.hex_code || '#ccc' }}
                  />
                  {itemColor.name}
                </span>
              )}
              {itemSize && (
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                  {itemSize.name}
                </span>
              )}
            </div>
          )}
          {shouldShowPricing && isPriceModified && (
            <button
              type="button"
              onClick={() => onResetPrice(cartItem.id)}
              className="inline-flex items-center gap-1 mt-1 text-xs text-amber-600 dark:text-amber-400"
            >
              <RotateCcw className="h-3 w-3" />
              Reset price
            </button>
          )}
          {shipmentMode === 'reserve' && warehouseLocationName && (
            <p className="flex items-center gap-1 mt-1 text-xs text-amber-700 dark:text-amber-300">
              <Warehouse className="h-3 w-3" />
              {warehouseLocationName}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(cartItem.id)}
          className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
          aria-label="Remove item"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center justify-between gap-2">
        {shouldShowPricing ? (
          isEditing ? (
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-500">{'\u20AC'}</span>
              <input
                type="number"
                value={editingPriceValue}
                onChange={e => onPriceEditChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') onPriceEditSave(cartItem.id)
                  if (e.key === 'Escape') onPriceEditCancel()
                }}
                className="w-16 px-1.5 py-0.5 text-sm text-right border border-teal-400 rounded bg-white dark:bg-gray-900"
                autoFocus
                step="0.01"
                min="0"
              />
              <button type="button" onClick={() => onPriceEditSave(cartItem.id)} className="p-1 text-green-600">
                <Check className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={onPriceEditCancel} className="p-1 text-gray-400">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onPriceEditStart(cartItem.id, cartItem.sell_price || 0)}
              className="group flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400"
            >
              {'\u20AC'}{cartItem.sell_price?.toFixed(2)}
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100" />
            </button>
          )
        ) : (
          <span />
        )}

        <div className="flex items-center gap-1 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
          <button
            type="button"
            onClick={() => onUpdateQuantity(cartItem.id, cartItem.cartQuantity - 1)}
            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-7 text-center text-sm font-semibold">{cartItem.cartQuantity}</span>
          <button
            type="button"
            onClick={() => onUpdateQuantity(cartItem.id, cartItem.cartQuantity + 1)}
            disabled={!allowOverStock && cartItem.cartQuantity >= availableQty}
            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>

        {shouldShowPricing && (
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100 min-w-[52px] text-right">
            {'\u20AC'}{cartItem.lineTotal.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  )
}
